import {get} from "lodash"
import moment from "moment-timezone"
import {TableColumns} from "types/common"
import {roundTwoDigits} from "./common"

export type ExportCellColumn = Pick<TableColumns, "field" | "fieldType" | "render" | "renderText">

export type CsvSanitizeMode = "none" | "quote" | "alphanumeric"

export type FormatExportCellOptions = {
    item: Record<string, unknown>
    col: ExportCellColumn
    exportType: string
    dateFormat?: string
    formatNumbers?: boolean
    dateFieldUsesUtc?: boolean
    useColumnRender?: boolean
    csvSanitize?: CsvSanitizeMode
    csvSanitizeExcludeFields?: string[]
    csvQuoteEmptyValues?: boolean
    replaceNewlines?: boolean
}

const getFieldValue = (item: Record<string, unknown>, col: ExportCellColumn): unknown => {
    return get(item, col.field)
}

const getRenderedValue = (item: Record<string, unknown>, col: ExportCellColumn): unknown => {
    const fieldValue = getFieldValue(item, col)

    if (col.renderText) {
        return col.renderText(fieldValue, item)
    }
    if (col.render) {
        return col.render(fieldValue, item)?.toString?.()
    }
    return fieldValue
}

const formatDateValue = (value: unknown, dateFormat: string, useUtc: boolean): string => {
    if (!value) {
        return ""
    }
    return useUtc ? moment.utc(String(value)).format(dateFormat) : moment(String(value)).format(dateFormat)
}

const sanitizeCsvValue = (value: string, mode: CsvSanitizeMode, quoteEmptyValues = false): string => {
    if (mode === "quote") {
        if (!value) {
            return quoteEmptyValues ? `""` : ""
        }
        return `"${value.replaceAll(/[|,;"]/g, "")}"`
    }
    if (mode === "alphanumeric") {
        return value.replaceAll(/[^a-zA-Z ]/g, "")
    }
    return value
}

export const formatExportCellValue = ({
    item,
    col,
    exportType,
    dateFormat = "MM/DD/YYYY",
    formatNumbers = false,
    dateFieldUsesUtc = false,
    useColumnRender = false,
    csvSanitize = "none",
    csvSanitizeExcludeFields = [],
    csvQuoteEmptyValues = false,
    replaceNewlines = false
}: FormatExportCellOptions): string => {
    if (col.fieldType === "dateUtc") {
        return formatDateValue(getFieldValue(item, col), dateFormat, true)
    }
    if (col.fieldType === "date") {
        return formatDateValue(getFieldValue(item, col), dateFormat, dateFieldUsesUtc)
    }

    const rawValue = useColumnRender ? getRenderedValue(item, col) : getFieldValue(item, col)

    if (formatNumbers && typeof rawValue === "number") {
        return roundTwoDigits(rawValue).toString()
    }

    let value = rawValue?.toString() ?? ""

    if (replaceNewlines) {
        value = value.replace(/\r\n|\n|\r/g, " ")
    }

    if (exportType === "csv" && csvSanitize !== "none" && !csvSanitizeExcludeFields.includes(col.field)) {
        return sanitizeCsvValue(value, csvSanitize, csvQuoteEmptyValues)
    }

    return value
}

export const buildExportRows = (
    data: Record<string, unknown>[],
    columns: ExportCellColumn[],
    options: Omit<FormatExportCellOptions, "item" | "col">
): string[][] => {
    return data.map((item) => columns.map((col) => formatExportCellValue({item, col, ...options})))
}
